import { DiscordSDK } from "@discord/embedded-app-sdk";

export type BootstrapStage =
  | "checking"
  | "discord-ready"
  | "authorizing"
  | "authenticating"
  | "guest"
  | "complete";

export interface ActivityIdentity {
  id: string;
  displayName: string;
  avatarUrl?: string;
  instanceId: string;
  guildId?: string;
  channelId?: string;
  ticket?: string;
  accessToken?: string;
  isGuest: boolean;
}

export interface DiscordBootstrap {
  identity: ActivityIdentity;
  sdk: DiscordSDK | null;
}

type TokenResponse = {
  access_token?: string;
  accessToken?: string;
  ticket?: string;
  session_ticket?: string;
  sessionTicket?: string;
  instance_id?: string;
  instanceId?: string;
  user?: {
    id?: string;
    username?: string;
    global_name?: string | null;
    display_name?: string | null;
    avatar?: string | null;
    avatar_url?: string | null;
    displayName?: string;
    avatarUrl?: string | null;
    guest?: boolean;
  };
};

const SESSION_KEY = "tidebreak.preview-player";

async function publicConfig(): Promise<{
  clientId: string;
  guestAllowed: boolean;
  oauthConfigured: boolean;
  oauthMode: "secret" | "pkce" | "guest";
}> {
  const configured = import.meta.env.VITE_DISCORD_CLIENT_ID?.trim();
  try {
    const response = await fetch("/api/config", { headers: { accept: "application/json" } });
    if (response.ok) {
      const result = (await response.json()) as {
        clientId?: string;
        guestAllowed?: boolean;
        oauthConfigured?: boolean;
        oauthMode?: "secret" | "pkce" | "guest";
      };
      return {
        clientId: result.clientId?.trim() || configured || "",
        guestAllowed: result.guestAllowed === true,
        oauthConfigured: result.oauthConfigured === true,
        oauthMode:
          result.oauthMode === "secret" || result.oauthMode === "pkce"
            ? result.oauthMode
            : "guest",
      };
    }
  } catch {
    // The Vite-only preview has no API. The environment value still works.
  }
  return {
    clientId: configured || "",
    guestAllowed: !import.meta.env.PROD,
    oauthConfigured: false,
    oauthMode: "guest",
  };
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const random = crypto.getRandomValues(new Uint8Array(48));
  const verifier = base64Url(random);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

function queryInstanceId(): string | undefined {
  const search = new URLSearchParams(window.location.search);
  const value = search.get("instance_id") ?? search.get("instanceId") ?? undefined;
  return value && /^[A-Za-z0-9_-]{1,128}$/.test(value) ? value : undefined;
}

function previewInstanceId(): string {
  const existing = queryInstanceId();
  if (existing) return existing;
  const generated = `preview_${crypto.randomUUID().replaceAll("-", "")}`;
  const url = new URL(window.location.href);
  url.searchParams.set("instance_id", generated);
  window.history.replaceState(null, "", url);
  return generated;
}

function isDiscordFrame(): boolean {
  const search = new URLSearchParams(window.location.search);
  return (
    window.self !== window.top ||
    search.has("frame_id") ||
    search.has("platform")
  );
}

async function jsonPost(path: string, body: unknown): Promise<TokenResponse> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `${path} returned ${response.status}`);
  }

  return (await response.json()) as TokenResponse;
}

function storedGuest(): { id: string; displayName: string } {
  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    if (value) {
      const parsed = JSON.parse(value) as { id?: string; displayName?: string };
      if (parsed.id && parsed.displayName) {
        return { id: parsed.id, displayName: parsed.displayName };
      }
    }
  } catch {
    // Storage can be unavailable inside hardened browser frames.
  }

  const suffix = Math.floor(100 + Math.random() * 900);
  const guest = {
    id: `guest-${crypto.randomUUID()}`,
    displayName: `Tide Guest ${suffix}`,
  };
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(guest));
  } catch {
    // A stable identity is helpful, but not required for preview mode.
  }
  return guest;
}

async function guestBootstrap(
  guestAllowed: boolean,
  onStage?: (stage: BootstrapStage, detail?: string) => void,
  options: { sdk?: DiscordSDK | null; instanceId?: string } = {},
): Promise<DiscordBootstrap> {
  if (!guestAllowed) {
    throw new Error(
      "This preview does not allow guest park passes. Launch TideBreak from a Discord voice channel.",
    );
  }
  onStage?.("guest", "Opening a preview lane…");
  const guest = storedGuest();
  const fallbackInstance = options.instanceId ?? previewInstanceId();

  let session: TokenResponse = {};
  try {
    session = await jsonPost("/api/oauth/token", {
      guestName: guest.displayName,
      guestKey: guest.id,
      instanceId: fallbackInstance,
    });
  } catch (error) {
    // Local Vite previews may not have the multiplayer server running yet. The
    // socket will surface a useful connection message while the diorama loads.
    if (import.meta.env.PROD) throw error;
  }

  const serverUser = session.user;
  onStage?.("complete", "Preview lane ready");
  return {
    sdk: options.sdk ?? null,
    identity: {
      id: serverUser?.id ?? guest.id,
      displayName:
        serverUser?.displayName ??
        serverUser?.display_name ??
        serverUser?.global_name ??
        serverUser?.username ??
        guest.displayName,
      avatarUrl: serverUser?.avatarUrl ?? serverUser?.avatar_url ?? undefined,
      instanceId: session.instanceId ?? session.instance_id ?? fallbackInstance,
      ticket: session.ticket ?? session.sessionTicket ?? session.session_ticket,
      isGuest: true,
    },
  };
}

function discordAvatarUrl(userId: string, avatar?: string | null): string | undefined {
  if (!avatar) return undefined;
  return `https://cdn.discordapp.com/avatars/${encodeURIComponent(userId)}/${encodeURIComponent(avatar)}.png?size=128`;
}

export async function bootstrapDiscord(
  onStage?: (stage: BootstrapStage, detail?: string) => void,
): Promise<DiscordBootstrap> {
  onStage?.("checking", "Finding your Discord party…");
  const config = await publicConfig();

  if (!config.clientId || !isDiscordFrame()) {
    return guestBootstrap(config.guestAllowed, onStage);
  }

  const sdk = new DiscordSDK(config.clientId);
  try {
    await sdk.ready();
    onStage?.("discord-ready", "Discord connected");
    const sdkContext = sdk as unknown as {
      instanceId?: string;
      guildId?: string;
      channelId?: string;
    };
    const instanceId = sdkContext.instanceId ?? queryInstanceId();
    if (!instanceId) throw new Error("Discord did not provide an Activity instance ID.");

    // A verified anonymous park pass keeps the Activity playable while a new
    // client secret is awaiting Discord MFA. The server validates the live
    // Activity instance with the bot token; normal OAuth takes over
    // automatically as soon as the secret is configured.
    if (!config.oauthConfigured) {
      return guestBootstrap(config.guestAllowed, onStage, { sdk, instanceId });
    }

    onStage?.("authorizing", "Asking Discord for your nickname…");

    // PKCE binds Discord's one-time authorization code to this exact Activity
    // frame. It also lets a Developer Portal Public Client authenticate safely
    // when a confidential client secret is unavailable.
    const pkce = await createPkcePair();

    const { code } = await sdk.commands.authorize({
      client_id: config.clientId,
      response_type: "code",
      state: "tidebreak",
      prompt: "none",
      scope: ["identify", "guilds.members.read"],
      code_challenge: pkce.challenge,
      code_challenge_method: "S256",
    });

    const token = await jsonPost("/api/oauth/token", {
      code,
      codeVerifier: pkce.verifier,
      instanceId,
      guildId: sdkContext.guildId ?? null,
    });
    const accessToken = token.access_token ?? token.accessToken;
    if (!accessToken) throw new Error("The Activity server did not return a Discord access token.");

    onStage?.("authenticating", "Loading your park pass…");
    const authenticated = await sdk.commands.authenticate({ access_token: accessToken });
    if (!authenticated?.user?.id) throw new Error("Discord authentication did not return a user.");

    const user = authenticated.user;
    const tokenUser = token.user;
    const displayName =
      tokenUser?.display_name ??
      tokenUser?.global_name ??
      tokenUser?.displayName ??
      user.global_name ??
      tokenUser?.username ??
      user.username;

    onStage?.("complete", "Party found");
    return {
      sdk,
      identity: {
        id: user.id,
        displayName,
        avatarUrl:
          tokenUser?.avatarUrl ??
          tokenUser?.avatar_url ??
          discordAvatarUrl(user.id, tokenUser?.avatar ?? user.avatar),
        instanceId:
          token.instanceId ??
          token.instance_id ??
          sdkContext.instanceId ??
          queryInstanceId() ??
          `discord-${sdkContext.channelId ?? user.id}`,
        guildId: sdkContext.guildId,
        channelId: sdkContext.channelId,
        ticket: token.ticket ?? token.sessionTicket ?? token.session_ticket,
        accessToken,
        isGuest: false,
      },
    };
  } catch (error) {
    const reason = error instanceof Error ? error.message : "Discord setup failed.";
    throw new Error(
      `${reason} Close and reopen the Activity from a voice channel, then try again.`,
      { cause: error },
    );
  }
}

export async function inviteFriends(sdk: DiscordSDK | null): Promise<"dialog" | "copied"> {
  if (sdk) {
    await sdk.commands.openInviteDialog();
    return "dialog";
  }

  await navigator.clipboard.writeText(window.location.href);
  return "copied";
}
